/**
 * Audit Logger
 * 
 * Logs admin actions and security events for compliance and monitoring
 * OWASP A09: Security Logging and Monitoring
 */

import { safeFrom, safeAuth, isSupabaseAvailable } from './safe-supabase';
import { notConfiguredError } from './service-errors';

export type AuditAction = 
  | 'CREATE' 
  | 'UPDATE' 
  | 'DELETE' 
  | 'LOGIN' 
  | 'LOGOUT' 
  | 'LOGIN_FAILED'
  | 'PASSWORD_RESET'
  | 'PERMISSION_DENIED'
  | 'EXPORT'
  | 'IMPORT'
  | 'SETTINGS_CHANGE';

export type AuditEntityType = 
  | 'page'
  | 'event'
  | 'news'
  | 'gallery'
  | 'sponsor'
  | 'partner'
  | 'testimonial'
  | 'fact'
  | 'site_settings'
  | 'user'
  | 'admin';

interface AuditLogEntry {
  action: AuditAction;
  entity_type: AuditEntityType;
  entity_id?: string;
  old_value?: Record<string, unknown>;
  new_value?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

/**
 * Get client IP address (for server-side use)
 */
const getClientIP = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  // In client-side, we can't get real IP
  // This would be set server-side
  return null;
};

/**
 * Get user agent
 */
const getUserAgent = (): string | null => {
  if (typeof window === 'undefined' || !window.navigator) {
    return null;
  }
  return window.navigator.userAgent;
};

/**
 * Create an audit log entry
 */
export const createAuditLog = async (entry: AuditLogEntry): Promise<void> => {
  if (!isSupabaseAvailable()) {
    console.warn('Audit logging skipped: Supabase not configured');
    return;
  }

  try {
    const { user } = await safeAuth.getUser();
    
    const { error } = await safeFrom('audit_logs').insert([{
      user_id: user?.user?.id || null,
      action: entry.action,
      entity_type: entry.entity_type,
      entity_id: entry.entity_id || null,
      old_value: entry.old_value || null,
      new_value: entry.new_value || null,
      metadata: entry.metadata || null,
      ip_address: getClientIP(),
      user_agent: getUserAgent(),
    }]);

    if (error) {
      console.error('Failed to create audit log:', error);
      // Don't throw - audit logging should not break the application
    }
  } catch (error) {
    console.error('Error creating audit log:', error);
    // Silently fail - audit logging should not break the application
  }
};

/**
 * Log authentication events
 */
export const auditAuth = {
  login: async (userId: string, metadata?: Record<string, unknown>) => {
    await createAuditLog({
      action: 'LOGIN',
      entity_type: 'user',
      entity_id: userId,
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString(),
      },
    });
  },

  loginFailed: async (email: string, reason?: string) => {
    await createAuditLog({
      action: 'LOGIN_FAILED',
      entity_type: 'user',
      metadata: {
        email,
        reason,
        timestamp: new Date().toISOString(),
      },
    });
  },

  logout: async (userId: string) => {
    await createAuditLog({
      action: 'LOGOUT',
      entity_type: 'user',
      entity_id: userId,
    });
  },

  passwordReset: async (userId: string) => {
    await createAuditLog({
      action: 'PASSWORD_RESET',
      entity_type: 'user',
      entity_id: userId,
    });
  },
};

/**
 * Log content management actions
 */
export const auditContent = {
  create: async (entityType: AuditEntityType, entityId: string, data: Record<string, unknown>) => {
    await createAuditLog({
      action: 'CREATE',
      entity_type: entityType,
      entity_id: entityId,
      new_value: data,
    });
  },

  update: async (
    entityType: AuditEntityType,
    entityId: string,
    oldData: Record<string, unknown>,
    newData: Record<string, unknown>
  ) => {
    await createAuditLog({
      action: 'UPDATE',
      entity_type: entityType,
      entity_id: entityId,
      old_value: oldData,
      new_value: newData,
    });
  },

  delete: async (entityType: AuditEntityType, entityId: string, data: Record<string, unknown>) => {
    await createAuditLog({
      action: 'DELETE',
      entity_type: entityType,
      entity_id: entityId,
      old_value: data,
    });
  },
};

/**
 * Log settings changes
 */
export const auditSettings = {
  change: async (
    settingKey: string,
    oldValue: unknown,
    newValue: unknown,
    metadata?: Record<string, unknown>
  ) => {
    await createAuditLog({
      action: 'SETTINGS_CHANGE',
      entity_type: 'site_settings',
      metadata: {
        setting_key: settingKey,
        old_value: oldValue,
        new_value: newValue,
        ...metadata,
      },
    });
  },
};

/**
 * Log permission denials
 */
export const auditPermission = {
  denied: async (action: string, resource: string, reason?: string) => {
    await createAuditLog({
      action: 'PERMISSION_DENIED',
      entity_type: 'user',
      metadata: {
        attempted_action: action,
        resource,
        reason,
        timestamp: new Date().toISOString(),
      },
    });
  },
};

/**
 * Get audit logs (admin only)
 */
export const getAuditLogs = async (options: {
  limit?: number;
  offset?: number;
  action?: AuditAction;
  entityType?: AuditEntityType;
  userId?: string;
  startDate?: Date;
  endDate?: Date;
} = {}) => {
  if (!isSupabaseAvailable()) {
    throw notConfiguredError('Supabase');
  }

  const {
    limit = 100,
    offset = 0,
    action,
    entityType,
    userId,
    startDate,
    endDate,
  } = options;

  let query = safeFrom('audit_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (action) {
    query = query.eq('action', action) as any;
  }

  if (entityType) {
    query = query.eq('entity_type', entityType) as any;
  }

  if (userId) {
    query = query.eq('user_id', userId) as any;
  }

  if (startDate) {
    query = query.gte('created_at', startDate.toISOString()) as any;
  }

  if (endDate) {
    query = query.lte('created_at', endDate.toISOString()) as any;
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data || [];
};

export default {
  create: createAuditLog,
  auth: auditAuth,
  content: auditContent,
  settings: auditSettings,
  permission: auditPermission,
  getLogs: getAuditLogs,
};





